<script setup lang="ts">
import {ref} from "vue";
import {CourseInfo} from "@/types";
import {dayjs, ElNotification} from "element-plus";
import {CourseService} from "@/api/api.ts";
import {TimeFormatter} from "../../utils/common.ts";

// 上下架时间
const startDate = ref<Date>();
const endDate = ref<Date>();
// 有效类型
const validType = ref(2)
// 截止日期
const untilDate = ref('')
// 固定天数
const constDate = ref(365)
// 是否开放试看
// const is_try = ref(false)
// 是否不限定购买
const not_sign = ref(false)
// 课程基本信息
const courseInfo = ref<CourseInfo>();
let courseId: number
const visible = ref(false)
const show = (id: number) => {
  courseId = id
  getCourseInfo()
  visible.value = true
}

const hide = () => {
  visible.value = false
}

const emit = defineEmits(["submit"])

const calValidDate = (): number | undefined => {
  if (courseInfo.value?.info.type === 4) {
    return undefined
  }
  if (validType.value === 1) {
    // 永久
    return 1
  } else if (validType.value === 2) {
    // 截止日期
    if (untilDate.value) {
      return dayjs(untilDate.value).startOf('day').unix()
    } else {
      return undefined
    }
  } else if (validType.value === 3) {
    return constDate.value
  }
  return undefined
}

const validate = () => {
  if (courseInfo.value?.info.type === 4) {
    return true
  }
  if (validType.value === 2) {
    // 截止日期
    if (!untilDate.value) {
      ElNotification.error({
        message: '请填写购课后有效期',
      })
      return false
    }
  } else if (validType.value === 3) {
    if (constDate.value < 3) {
      ElNotification.error({
        message: '购买后有效期不能少于3天',
      })
      return false
    }
  }
  return true
}

const onSubmit = () => {
  if (!startDate.value || !endDate.value) {
    return ElNotification.error({
      message: '请选择课程上架时间',
    })
  }
  const start_time = dayjs(startDate.value).startOf('day').unix()
  const end_time = dayjs(endDate.value).endOf('day').unix()
  if (!start_time || !end_time) {
    return ElNotification.error({
      message: '请选择课程上架时间',
    })
  }
  if (start_time >= end_time) {
    return ElNotification.error({
      message: '上架时间不能晚于下架时间',
    })
  }
  if (!validate()) {
    return
  }
  const info = {
    course_id: courseId,
    thumb_image: courseInfo.value?.info.thumb_image || '',
    putaway_time: start_time,
    down_time: end_time,
    buy_valid_date: calValidDate(),
    // is_try: is_try.value ? 1 : 0,
    is_sign: !not_sign.value ? 1 : 0,
  }
  emit("submit", info)
}

const onCancel = () => {
  hide()
}

// 获取课程基本信息
const getCourseInfo = async () => {
  try {
    const res = await CourseService.getCourseInfo({
      id: courseId
    })
    if (res.data.code === 200) {
      courseInfo.value = res.data.data;
    } else {
      ElNotification.error({
        title: '获取课程详情失败',
        message: res.data.msg,
        type: 'error',
      })
    }
  } catch (error) {
    console.log(error)
  }
}

defineExpose({
  show, hide
})
</script>

<template>
  <el-dialog width="auto" v-model="visible" title="" :close-on-click-modal="false" align-center class="transparent-bg"
             :show-close="false">
    <div class="flex items-start">
      <!-- 左边  -->
      <div class="bg-#f5f5f5 w-400px mr-24px bg-#f5f5f5 pb-16px" v-if="courseInfo">
        <div class="flex justify-center items-center h-44px font-bold text-18px">
          {{
            courseInfo.info.type === 1 ? `直播课详情` : courseInfo.info.type === 2 ? `录播课详情` :
                courseInfo.info.type === 3 ? `系列课详情` : courseInfo.info.type === 4 ? `组合课详情` : ``
          }}
        </div>
        <el-image :src="courseInfo.info.thumb_image" style="width: 400px; height: 225px"/>
        <div class="bg-#f6f7f9 pt-20px">
          <div class="mx-16px rounded-md bg-white py-16px px-12px">
            <div class="text-#333333 text-19px font-bold">{{ courseInfo.info.title }}</div>
            <div class="text-#666666 text-13px mt-8px">
              <template v-if="courseInfo.info.type === 1 || courseInfo.info.type === 2">
                {{ `共${courseInfo.info.total_section}讲 ` }}
              </template>
              <template v-if="courseInfo.info.type === 1">
                {{ `· 每讲${courseInfo.info.duration}分钟` }}
              </template>
            </div>
            <div class="text-#666666 text-13px mt-8px">
              {{ courseInfo.info.sign_time }}
            </div>
            <div class="mt-24px text-#ff5916">
              {{ `￥${courseInfo.info.price}` }}
            </div>
          </div>
          <div class="bg-white p-16px mx-16px rounded-md mt-20px" v-if="courseInfo.info.type === 3">
            <div class="text-18px font-bold text-#333333 mb-8px">包含的课程</div>
            <div v-for="item in courseInfo.section" class="flex mt-18px">
              <el-image :src="item.thumb_image" style="width: 108px; height: 70px"/>
              <div class="ml-10px flex flex-col">
                <div class="text-#333333 text-17px font-bold">{{ item.title }}</div>
                <div class="flex-1"></div>
                <div class="text-#333333 text-13px mt-8px line-through">{{ `原价￥${item.price}` }}</div>
                <div class="text-#999999 text-11px mt-6px">{{ `${dayjs(item.down_time * 1000).format('YYYY年M月D日')}下架`}}</div>
              </div>
            </div>
          </div>
          <div class="bg-white p-16px mx-16px rounded-md mt-20px" v-if="courseInfo.info.type === 4">
            <div class="text-18px font-bold text-#333333 mb-8px">参与该组合的课程</div>
            <div v-for="item in courseInfo.section" class="flex mt-18px">
              <el-image :src="item.thumb_image" style="width: 108px; height: 70px"/>
              <div class="ml-10px">
                <div class="text-#333333 text-17px font-bold">{{ item.title }}</div>
                <div class="text-#FB7139 text-17px mt-8px">{{ `￥${item.price}` }}</div>
              </div>
            </div>
          </div>
          <div class="mt-40px inline-block">
            <div class="text-#333333 text-18px font-bold ml-16px">课程详情</div>
            <div class="bg-#ffc000 h-10px w-full mt-[-8px]"/>
          </div>
          <div class="mx-16px mt-18px">{{ courseInfo.info.short_detail }}</div>
          <!-- 直播录播  -->
          <div class="mt-40px inline-block" v-if="courseInfo.info.type === 1 || courseInfo.info.type === 2">
            <div class="text-#333333 text-18px font-bold ml-16px">课程表</div>
            <div class="bg-#ffc000 h-10px w-full mt-[-8px]"/>
          </div>
          <div class="mx-16px rounded-md bg-white py-16px px-12px mt-14px" v-if="courseInfo.info.type === 1 || courseInfo.info.type === 2">
            <template v-for="(item) in courseInfo.section">
              <div class="flex mt-18px">
                <div class="text-#999999 font-bold text-18px ">
                  {{ item.section_sort > 10 ? `${item.section_sort}` : `0${item.section_sort}` }}
                </div>
                <div class="ml-16px">
                  <div class="text-17px font-bold text-#333333">{{ item.title }}</div>
                  <div class="mt-5px text-13px text-#666666">共{{ TimeFormatter.secondsFormat(item.duration) }} </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
      <div class="rounded p-24px bg-white w-484px">
        <div class="text-#000000d9 text-1.25em">发布前请确认信息填写正确
        </div>
        <el-form class="mt-32px">
          <el-form-item label="课程上下架时间">
            <el-date-picker v-model="startDate" type="date" placeholder="课程上架日期">
            </el-date-picker>
            <el-date-picker v-model="endDate" type="date" placeholder="课程下架日期" class="mt-.5rem">
            </el-date-picker>
          </el-form-item>
          <el-form-item label="购买后有效期" v-if="courseInfo && courseInfo.info.type !== 4">
            <el-radio-group v-model="validType" style="flex-direction: column; align-items: flex-start">
              <!-- 暂时隐藏永久有效  -->
              <!--<el-radio :value="1">永久有效</el-radio>-->
              <el-radio :value="2">
                <el-date-picker type="date" placeholder="截至日期" v-model="untilDate"></el-date-picker>
                前有效
              </el-radio>
              <el-radio :value="3" class="mt-1rem">
                购买后
                <el-input type="number" v-model="constDate" style="width: 5rem"></el-input>
                天有效
              </el-radio>
            </el-radio-group>
          </el-form-item>
<!--          <el-form-item label="是否开放试看" v-if="courseInfo && courseInfo.info.type === 2">-->
<!--            <el-checkbox v-model="is_try" label="第一讲免费试看" size="large" class="mt-[-3px]"/>-->
<!--          </el-form-item>-->
          <el-form-item label="是否限定购买" v-if="courseInfo && courseInfo.info.type !== 4">
            <el-checkbox v-model="not_sign" label="限定购买(勾选此项则只能老师添加)" size="large" class="mt-[-3px]"/>
          </el-form-item>
          <el-form-item label=" ">
            <el-button type="primary" @click="onSubmit">
              确认发布
            </el-button>
            <el-button type="primary" plain @click="onCancel">
              取消
            </el-button>
          </el-form-item>
        </el-form>
      </div>
    </div>
  </el-dialog>
</template>

<style>
.transparent-bg {
  --el-dialog-bg-color: transparent;
  --el-dialog-box-shadow: none;
}
</style>
